import { TextMarkup } from "@shared/Components/TextMarkup/TextMarkup";
import {
    PostcardActionAttributes,
    PostcardActionButtonsHandlersType,
    PostcardDataType,
    PostComponentOptionsType,
} from "../../Components/Post/Post.types";
import { useWindowSize } from "@infrastructure/Hooks/useWindowSize";
import { GroupTypeEnum } from "../GroupDetailsPage/Group.types";
import { SERVICE_MODALS_OPTIONS_STATES } from "../../Components/Modals/ServiceFunctionsModal.types";
import * as React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { isDeviceOutputMobileOrMobileApp } from "../../../Domain/DeviceOutputState";
import { useGroupSelector } from "../../Components/GroupSelector/useGroupSelector";
import { shortAndShareEntityOnMobile } from "@infrastructure/Helpers/SharingEntity";
import { useInView } from "@infrastructure/Hooks/useInView";
import { OPEN_PRESENTER } from "../../Components/Post/PostForm/events";
import {
    addSubscriber,
    removeSubscriber,
} from "@infrastructure/Events/EventBus";
import { PostReactionsListModal } from "../../Components/Modals/ServiceModals/ReactionsListModal/PostReactionsListModal";
import {
    CardBoxed,
    PostComponentsSection,
} from "../../Components/Post/Post.styles";
import { PostHeaderDefault } from "../../Components/Post/Header/PostHeaderDefault";
import ImageGrid, {
    IMAGE_GRID_TYPES,
    ImageType,
} from "../../Components/ImageGrid/ImageGrid";
import { Box, Stack } from "@mui/material";
import { PromoPostActionButtons } from "../../Components/Post/PostActions/PostActionButtons";
import { usePostDetailsModalStore } from "../../Components/Modals/PostDetailsModal/PostDetailsModalStore";
import {
    BoxActions,
    BoxClickPrevent,
    BoxMainContent,
    PostContentBox,
    PostImageGrid,
} from "./PostsPromoSlider.styles";
import { PollTypesEnum } from "@shared/Types/Polls";
import { TEXT_ELLIPSIS } from "@shared/styles";
import { PostPollPreview } from "../../Components/Post/PostPoll/PostPollPreview";
import { LinkoutButton } from "../../Components/Post/PostActions/LinkoutButton";

export function PostsPromoSliderItem({
    id,
    data,
    showGroupName,
    postActions,
}: PostComponentOptionsType) {
    const { isMobileScreenSizeFlag } = useWindowSize();
    const { mainGroupId } = useGroupSelector();
    const showDesktopSharingOptions = !isDeviceOutputMobileOrMobileApp();

    const selectedPost: Pick<
        PostComponentOptionsType,
        "id" | "data" | "postActions"
    > = useMemo(
        () => ({
            id: id,
            data: data,
            postActions: postActions,
        }),
        [data, id, postActions],
    );

    const onShareButtonClicked = useCallback(() => {
        if (showDesktopSharingOptions) {
            postActions.setSelectedPost(selectedPost);
            postActions.setIsPostOptionsModalOpen(true);
            postActions.setModalCurrentAction(
                SERVICE_MODALS_OPTIONS_STATES.SHARING_POST,
            );
        } else {
            mainGroupId &&
                shortAndShareEntityOnMobile(
                    mainGroupId,
                    id.toString(),
                    data.group!.name,
                );
        }
    }, [
        data.group,
        id,
        mainGroupId,
        postActions,
        selectedPost,
        showDesktopSharingOptions,
    ]);

    const postActionOpts: PostcardActionAttributes = {
        postId: data.id,
        postData: data.postData as PostcardDataType,
        groupType: data.group?.type as GroupTypeEnum,
        ...((postActions.onCommentButtonClick
            ? { onCommentButtonClick: postActions.onCommentButtonClick }
            : {}) as PostcardActionButtonsHandlersType),
        showCommentButton: false,
        isInsidePostDetails: postActions.isInsidePostDetails,
        commentButtonText: "Kommentieren",
        createReactOnPost: postActions.createReactOnPost,
        onReportButtonClick: () => {},
        showReportOption: false,
        onShareButtonClicked,
        showPollStats: false,
        showShareButton: false,
    };

    function onSettingsClickHandler(e: React.MouseEvent<HTMLElement>) {
        e.stopPropagation();
        postActions.setSelectedPost(selectedPost);
        postActions.setIsPostOptionsModalOpen(true);
        postActions.setModalCurrentAction(
            SERVICE_MODALS_OPTIONS_STATES.POST_STARTING_OPTIONS,
        );
    }

    function onPostGroupTitleClickHandler(e: React.MouseEvent<HTMLElement>) {
        e.stopPropagation();
        postActions.onCardContentClickHandler?.(id.toString());
    }

    const cardContentPostOpts = {
        ...(postActions.onCardContentClickHandler
            ? {
                  onClick: () => {
                      postActions.onCardContentClickHandler?.(id.toString());
                  },
              }
            : {}),
    };

    const { isPostDetailsModalOpen } = usePostDetailsModalStore();
    const { ref, inView } = useInView();
    const videoRef = useRef<HTMLVideoElement>(null);

    const [onlyPlayOnce, setOnlyPlayOnce] = useState(postActions.autoPlayVideo);
    useEffect(() => {
        if (isPostDetailsModalOpen || !inView) {
            if (onlyPlayOnce) {
                videoRef.current?.play();
            } else if (!inView) {
                videoRef.current?.pause();
            }
        }
    }, [inView, isPostDetailsModalOpen, onlyPlayOnce]);

    useEffect(() => {
        if (!onlyPlayOnce) {
            return;
        }

        const handlePlaying = () => {
            setOnlyPlayOnce(false);
        };

        const videoElement = videoRef?.current;

        videoElement?.addEventListener("playing", handlePlaying);

        return () => {
            videoElement?.removeEventListener("playing", handlePlaying);
        };
    }, []);

    useEffect(() => {
        const subscriber = addSubscriber(OPEN_PRESENTER, () => {
            videoRef.current?.pause();
        });
        return () => {
            removeSubscriber(subscriber);
        };
    }, []);

    const [showReactionsModal, setShowReactionsModal] = useState(false);
    const hasMediaOrTypePoll = useMemo(
        () =>
            (data.postData.poll?.type &&
                [
                    PollTypesEnum.POLL_RATING,
                    PollTypesEnum.POLL_IMAGES,
                    PollTypesEnum.POLL_VISIBLE,
                    PollTypesEnum.POLL_QUIZ,
                ].includes(data.postData.poll.type)) ||
            !!data.postData.images?.length ||
            !!data.postData.videos?.length,
        [data.postData],
    );

    return (
        <>
            {showReactionsModal && (
                <PostReactionsListModal
                    open={showReactionsModal}
                    setOpen={setShowReactionsModal}
                    postId={data.id}
                />
            )}
            <CardBoxed
                height="calc(100% - 12px)"
                isBoxed={data.pageRelated?.boxed}
                {...cardContentPostOpts}
            >
                <Stack justifyContent="space-between" height="100%">
                    <PostHeaderDefault
                        openPostAvatarModal={onPostGroupTitleClickHandler}
                        onSettingsClickHandler={onSettingsClickHandler}
                        user={data.user}
                        group={data.group}
                        postData={data.postData}
                        onLocationClick={onPostGroupTitleClickHandler}
                        showGroupName={showGroupName}
                        onPostGroupTitleClickHandler={
                            onPostGroupTitleClickHandler
                        }
                        showClosingButton={false}
                        isHighlighted={data.status.isHighlighted}
                    />
                    <BoxMainContent>
                        <BoxClickPrevent />
                        {!!data.postData.content?.length &&
                            data.postData.poll?.type !==
                                PollTypesEnum.POLL_RATING && (
                                <PostContentBox>
                                    <TextMarkup
                                        sx={
                                            hasMediaOrTypePoll
                                                ? {
                                                      ...TEXT_ELLIPSIS,
                                                  }
                                                : {}
                                        }
                                        textToMarkup={data.postData.content}
                                    />
                                </PostContentBox>
                            )}
                        {data.postData.images &&
                            data.postData.images?.length > 0 && (
                                <PostImageGrid>
                                    <ImageGrid
                                        images={
                                            data.postData.images as ImageType[]
                                        }
                                        imageGridType={
                                            postActions.isInsidePostDetails &&
                                            isMobileScreenSizeFlag
                                                ? IMAGE_GRID_TYPES.SMALL
                                                : IMAGE_GRID_TYPES.DEFAULT
                                        }
                                    />
                                </PostImageGrid>
                            )}
                        {data.postData.videos &&
                            data.postData.videos?.length > 0 &&
                            !data.postData.isMediaUnderProcessing && (
                                <PostImageGrid
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                    }}
                                    ref={ref}
                                >
                                    <video
                                        poster={
                                            data.postData.videos[0].thumbnailUrl
                                        }
                                        ref={videoRef}
                                        controls
                                        controlsList={
                                            "nodownload noplaybackrate"
                                        }
                                        preload={"none"}
                                        playsInline
                                        style={{
                                            background: "black",
                                            width: "100%",
                                            minWidth: "100%",
                                        }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            e.preventDefault();
                                        }}
                                    >
                                        <source
                                            src={data.postData.videos[0].url}
                                        />
                                    </video>
                                </PostImageGrid>
                            )}
                        {data.postData?.poll?.pollOptions?.length && (
                            <PostPollPreview
                                postId={data.id}
                                data={data.postData?.poll}
                                content={data.postData.content}
                            />
                        )}
                        <Box marginTop={"8px"}>
                            {data.postData?.linkoutUrl && (
                                <LinkoutButton
                                    linkoutUrl={data.postData?.linkoutUrl}
                                />
                            )}
                        </Box>
                    </BoxMainContent>
                    <BoxActions>
                        <PostComponentsSection>
                            <PromoPostActionButtons
                                {...postActionOpts}
                                user={data.user}
                                postReactions={data.postData.postReactions}
                                pollsOptions={data.postData.poll?.pollOptions}
                            />
                        </PostComponentsSection>
                    </BoxActions>
                </Stack>
            </CardBoxed>
        </>
    );
}
